import { create } from "zustand"

import type { DashboardAction } from "./chat-types"
import { applyDashboardAction, type ChatState } from "./dashboard-actions"
import {
  DEFAULT_LAYOUT,
  persistLayout,
  readStoredLayout,
} from "./layout"
import { resolveRegionCode } from "./region-codes"
import { saveViewToStorage } from "./useViews"

export type ConnectionStatus = "connecting" | "connected" | "disconnected"
export type Tab = "cockpit" | "globe"
export type MapMode = "3d" | "2d"

const MAP_MODE_STORAGE_KEY = "supplylens-map-mode-v1"

function readStoredMapMode(): MapMode {
  if (typeof window === "undefined") return "3d"
  try {
    const raw = window.localStorage.getItem(MAP_MODE_STORAGE_KEY)
    if (raw === "3d" || raw === "2d") return raw
  } catch {
    // localStorage may be unavailable (private mode, disk full) — ignore silently.
  }
  return "3d"
}

function persistMapMode(mode: MapMode) {
  if (typeof window === "undefined") return
  try {
    window.localStorage.setItem(MAP_MODE_STORAGE_KEY, mode)
  } catch {
    // ignore
  }
}

interface AppState {
  tenant: string
  connection: ConnectionStatus
  setConnection: (s: ConnectionStatus) => void
  /* Active top-level tab — lifted from Dashboard so the command palette can
     switch tabs without prop-drilling. */
  tab: Tab
  setTab: (t: Tab) => void
  /* World-view rendering mode — 3d globe or 2d flat map. Persisted to
     localStorage so the user's preference survives reload. */
  mapMode: MapMode
  setMapMode: (m: MapMode) => void
  /* Palette visibility — global so the ⌘K keydown listener can toggle
     regardless of which component owns the key binding. */
  paletteOpen: boolean
  setPaletteOpen: (v: boolean) => void
  /* Reset-layout trigger — incrementing this nonce tells the cockpit to
     reset the grid. Keeps the reset-layout imperative action reachable
     from the palette without wiring a callback through props. */
  resetLayoutNonce: number
  triggerResetLayout: () => void
}

export const useApp = create<AppState>((set) => ({
  tenant: "default",
  connection: "connecting",
  setConnection: (s) => set({ connection: s }),
  tab: "cockpit",
  setTab: (t) => set({ tab: t }),
  mapMode: readStoredMapMode(),
  setMapMode: (m) => {
    persistMapMode(m)
    set({ mapMode: m })
  },
  paletteOpen: false,
  setPaletteOpen: (v) => set({ paletteOpen: v }),
  resetLayoutNonce: 0,
  triggerResetLayout: () => set((st) => ({ resetLayoutNonce: st.resetLayoutNonce + 1 })),
}))

/* Region deep-dive modal state.
   Panels call `openRegion("MENA")` / `openRegion("RED_SEA")` etc.; the
   Dashboard mounts <RegionDeepDive /> once and reads `activeRegion` to
   decide whether the overlay is visible. */
interface RegionState {
  activeRegion: string | null
  openRegion: (code: string) => void
  closedRegion: () => void
}

export const useRegion = create<RegionState>((set) => ({
  activeRegion: null,
  openRegion: (code) => set({ activeRegion: code }),
  closedRegion: () => set({ activeRegion: null }),
}))

// ---------- Realtime (SSE liveness/pulse layer) ----------

/* The SSE stream is a LIVENESS cue on top of the 45s poll, not a data source.
   Each inbound envelope bumps `pulse` (a monotonic counter the header dot
   watches to flash) and stamps `lastEvent`. Incident events additionally bump
   `incidentPulse` so the disruption feed can flag its header. Components key
   one-shot animations off the counters, so re-renders never re-trigger a flash
   for an event they've already seen. */
export interface RealtimeSignal {
  topic: string
  at: string
}

interface RealtimeState {
  /** Monotonic count of every realtime envelope received this session. */
  pulse: number
  /** Monotonic count of `*.incident_created` envelopes specifically. */
  incidentPulse: number
  /** Most recent envelope (topic + timestamp), or null before the first. */
  lastEvent: RealtimeSignal | null
  /** Record an inbound realtime signal; bumps the relevant pulse counters. */
  notePulse: (signal: RealtimeSignal) => void
}

/** Topic suffix that marks a new disruption incident across any module id. */
const INCIDENT_TOPIC = /\.signal\.incident_created$/

export const useRealtimeStore = create<RealtimeState>((set) => ({
  pulse: 0,
  incidentPulse: 0,
  lastEvent: null,
  notePulse: (signal) =>
    set((st) => ({
      pulse: st.pulse + 1,
      incidentPulse: INCIDENT_TOPIC.test(signal.topic)
        ? st.incidentPulse + 1
        : st.incidentPulse,
      lastEvent: signal,
    })),
}))

// ---------- Toasts ----------

export interface Toast {
  id: number
  text: string
  kind: "info" | "warn" | "success"
}

interface ToastState {
  toasts: Toast[]
  pushToast: (text: string, kind?: Toast["kind"]) => void
  dismissToast: (id: number) => void
}

let toastSeq = 0
const TOAST_TTL_MS = 5_000

export const useToasts = create<ToastState>((set) => ({
  toasts: [],
  pushToast: (text, kind = "info") => {
    const id = ++toastSeq
    set((st) => ({ toasts: [...st.toasts, { id, text, kind }] }))
    if (typeof window !== "undefined") {
      window.setTimeout(
        () => set((st) => ({ toasts: st.toasts.filter((t) => t.id !== id) })),
        TOAST_TTL_MS,
      )
    }
  },
  dismissToast: (id) => set((st) => ({ toasts: st.toasts.filter((t) => t.id !== id) })),
}))

// ---------- Chat store ----------

type ChatStore = ChatState & {
  applyAction: (a: DashboardAction) => void
  setLayout: (l: ChatState["layout"]) => void
  clearPanelFilter: (panelId: string, filterKey: string) => void
  hydrate: (partial: Partial<ChatState>) => void
}

const CHAT_STORAGE_KEY = "supplylens-chat-state-v1"

function loadChatState(): Partial<ChatState> {
  if (typeof window === "undefined") return {}
  try {
    const raw = window.localStorage.getItem(CHAT_STORAGE_KEY)
    if (!raw) return {}
    return JSON.parse(raw) as Partial<ChatState>
  } catch {
    return {}
  }
}

function saveChatState(s: ChatState): void {
  if (typeof window === "undefined") return
  try {
    window.localStorage.setItem(
      CHAT_STORAGE_KEY,
      JSON.stringify({
        dynamicPanels: s.dynamicPanels,
        panelFilters: s.panelFilters,
      }),
    )
  } catch {
    // quota — ignore
  }
}

/* Action types the reducer + UI genuinely implement. Anything else gets an
   honest "unsupported" toast instead of silently pretending success. */
const SUPPORTED_ACTIONS = new Set([
  "add_panel",
  "remove_panel",
  "move_panel",
  "set_panel_filter",
  "create_dynamic_panel",
  "download_file",
  "highlight_region",
  "save_view",
])

/* Side effects for actions whose behaviour lives outside the pure reducer:
   highlight_region opens the deep-dive modal (or toasts when the code can't
   be mapped), save_view persists a named layout to localStorage. */
function runActionSideEffects(action: DashboardAction, currentLayout: ChatState["layout"]) {
  const { pushToast } = useToasts.getState()

  switch (action.action_type) {
    case "highlight_region": {
      const payload = action.payload as { iso_code?: string; code?: string; region?: string }
      const raw = payload.iso_code ?? payload.code ?? payload.region ?? ""
      const resolved = resolveRegionCode(raw)
      if (resolved) {
        useRegion.getState().openRegion(resolved)
      } else {
        pushToast(
          `Region "${raw || "?"}" isn't supported for deep-dive yet — try a major economy (NL, US, CN…) or a waterway (SUEZ, RED_SEA).`,
          "warn",
        )
      }
      return
    }
    case "save_view": {
      const { name } = action.payload as { name?: string }
      const saved = saveViewToStorage(name ?? "", currentLayout)
      if (saved) {
        pushToast(`Layout saved as view "${saved.name}" — load it from the views menu.`, "success")
      } else {
        pushToast("Could not save view: a non-empty name is required.", "warn")
      }
      return
    }
    case "set_panel_filter": {
      const p = action.payload as { panel_id?: string; filter_key?: string; filter_value?: string }
      pushToast(`Filter applied to ${p.panel_id ?? "panel"}: ${p.filter_key} = ${p.filter_value}`, "info")
      return
    }
    default: {
      if (!SUPPORTED_ACTIONS.has(action.action_type)) {
        pushToast(`Dashboard action "${action.action_type}" isn't supported by this UI yet.`, "warn")
      }
    }
  }
}

export const useChat = create<ChatStore>((set, get) => {
  const seed = loadChatState()
  const initial: ChatState = {
    // Single source of truth for the rendered grid: hydrate from the same
    // localStorage key the grid persists to, falling back to defaults.
    layout: readStoredLayout() ?? DEFAULT_LAYOUT,
    dynamicPanels: seed.dynamicPanels ?? {},
    panelFilters: seed.panelFilters ?? {},
    actionsLog: [],
  }
  return {
    ...initial,
    applyAction: (a) => {
      runActionSideEffects(a, get().layout)
      set((st) => {
        const next = applyDashboardAction(st, a)
        saveChatState(next)
        if (next.layout !== st.layout) persistLayout(next.layout)
        return next
      })
    },
    setLayout: (l) =>
      set((st) => {
        persistLayout(l)
        const next = { ...st, layout: l }
        saveChatState(next)
        return next
      }),
    clearPanelFilter: (panelId, filterKey) =>
      set((st) => {
        const current = st.panelFilters[panelId]
        if (!current || !(filterKey in current)) return st
        const nextFilters = { ...current }
        delete nextFilters[filterKey]
        const panelFilters = { ...st.panelFilters, [panelId]: nextFilters }
        if (Object.keys(nextFilters).length === 0) delete panelFilters[panelId]
        const next = { ...st, panelFilters }
        saveChatState(next)
        return next
      }),
    hydrate: (partial) => set((st) => ({ ...st, ...partial })),
  }
})
